
import { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RichTextEditor } from "@/components/admin/RichTextEditor";

interface Provider {
  id: string;
  name: string;
  logo_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface ProviderFormProps {
  initialData?: Provider | null;
  onSuccess: () => void;
}

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Provider name must be at least 2 characters.",
  }),
  logo_url: z.string().url("Please enter a valid URL").optional(),
  seo_title: z.string().optional(),
  seo_content: z.string().optional()
});

export const ProviderForm = ({ initialData, onSuccess }: ProviderFormProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [seoPageId, setSeoPageId] = useState<string | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || '',
      logo_url: initialData?.logo_url || '',
      seo_title: '',
      seo_content: ''
    },
  });

  useEffect(() => {
    const fetchSeoContent = async () => {
      if (initialData?.id) {
        const { data } = await supabase
          .from('provider_seo_pages')
          .select('*')
          .eq('provider_id', initialData.id)
          .single();
        
        if (data) {
          setSeoPageId(data.id);
          form.setValue('seo_title', data.title);
          form.setValue('seo_content', data.content);
        }
      }
    };
    
    fetchSeoContent();
  }, [initialData, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      let providerId = initialData?.id;
      
      // Update or create provider
      if (initialData) {
        const { error } = await supabase
          .from('providers')
          .update({
            name: values.name,
            logo_url: values.logo_url,
            updated_at: new Date().toISOString()
          })
          .eq('id', initialData.id);
          
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('providers')
          .insert({
            name: values.name,
            logo_url: values.logo_url
          })
          .select()
          .single();
          
        if (error) throw error;
        providerId = data.id;
      }
      
      // Update or create SEO content
      if (providerId) {
        if (seoPageId) {
          // Update existing SEO record
          const { error: seoError } = await supabase
            .from('provider_seo_pages')
            .update({
              title: values.seo_title,
              content: values.seo_content
            })
            .eq('id', seoPageId);
            
          if (seoError) throw seoError;
        } else {
          // Insert new SEO record
          const { error: seoError } = await supabase
            .from('provider_seo_pages')
            .insert({
              provider_id: providerId,
              title: values.seo_title,
              content: values.seo_content
            });
            
          if (seoError) throw seoError;
        }
      }
      
      toast({
        title: initialData ? "Provider updated" : "Provider created",
        description: "The provider has been saved successfully.",
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error saving provider:', error);
      toast({
        title: "Error",
        description: "There was an error saving the provider.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Provider name" {...field} />
              </FormControl>
              <FormDescription>
                This is the provider's display name.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="logo_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Logo URL</FormLabel>
              <FormControl>
                <Input placeholder="URL to provider logo" {...field} />
              </FormControl>
              <FormDescription>
                URL for the provider's logo image.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="seo_title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>SEO Title</FormLabel>
              <FormControl>
                <Input placeholder="SEO title for the provider page" {...field} />
              </FormControl>
              <FormDescription>
                This title will be used for SEO purposes on the provider's page.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="seo_content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>SEO Content</FormLabel>
              <FormControl>
                <RichTextEditor 
                  content={field.value || ''} 
                  onChange={field.onChange} 
                />
              </FormControl>
              <FormDescription>
                This content will be displayed on the provider's page for SEO purposes.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : "Save Provider"}
        </Button>
      </form>
    </Form>
  );
};
